/**
 * CAMT.053 XML Parser — Bank-to-Customer Statement
 *
 * Parses ABN AMRO CAMT.053 exports and extracts incoming (credit) transactions
 * aggregated by booking date. Used for the Controle page bank column auto-fill.
 */

export interface Camt053Entry {
  date: string;        // YYYY-MM-DD (booking date)
  amount: number;
  isCredit: boolean;   // CRDT = true, DBIT = false
  description: string;
  senderName: string;  // debtor name
  filtered: boolean;   // true if excluded (Mollie/Geldmaat/internal)
  filterReason: string | null;
}

export interface Camt053Balance {
  amount: number;
  date: string;       // YYYY-MM-DD
  currency: string;   // 'EUR'
}

export interface Camt053Result {
  iban: string;
  entries: Camt053Entry[];
  creditsByDay: Map<string, number>; // date → total incoming amount (after filtering)
  filteredTotal: number;             // total amount excluded by filters
  filteredCount: number;             // number of transactions excluded
  openingBalance: Camt053Balance | null;
  closingBalance: Camt053Balance | null;
}

function getLocalText(parent: Element, localName: string): string | null {
  // Use getElementsByTagNameNS with wildcard to handle any namespace
  const els = parent.getElementsByTagNameNS('*', localName);
  return els.length > 0 ? els[0]!.textContent?.trim() || null : null;
}

function getLocalElements(parent: Element, localName: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', localName));
}

// Transactions from these sources are already tracked via other columns (Mollie, Contant)
// or are internal transfers — exclude them from the bank reconciliation total
const FILTERED_SENDERS: { pattern: RegExp; reason: string }[] = [
  { pattern: /mollie/i, reason: 'Mollie uitbetaling' },
  { pattern: /geldmaat|geldautomaat/i, reason: 'Contante storting' },
  { pattern: /mypos/i, reason: 'MyPos uitbetaling' },
];

function getFilterReason(senderName: string, description: string, additionalInfo: string): string | null {
  const text = `${senderName} ${description} ${additionalInfo}`;
  for (const { pattern, reason } of FILTERED_SENDERS) {
    if (pattern.test(text)) return reason;
  }
  return null;
}

export function parseCamt053(xmlString: string): Camt053Result {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, 'text/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Ongeldig XML bestand');
  }

  // Find the statement(s) — handle both camt.053.001.02 and .08
  const statements = getLocalElements(doc.documentElement, 'Stmt');
  if (statements.length === 0) {
    throw new Error('Geen bankafschrift (Stmt) gevonden. Is dit een CAMT.053 bestand?');
  }

  // Use first statement — extract IBAN
  const stmt = statements[0]!;
  const iban = getLocalText(stmt, 'IBAN') || 'Onbekend';

  // Parse all entries
  const entries: Camt053Entry[] = [];
  const ntryElements = getLocalElements(stmt, 'Ntry');

  for (const ntry of ntryElements) {
    const amountEl = ntry.getElementsByTagNameNS('*', 'Amt')[0];
    const amount = amountEl ? parseFloat(amountEl.textContent || '0') : 0;
    if (amount === 0) continue;

    const cdtDbtInd = getLocalText(ntry, 'CdtDbtInd');
    const isCredit = cdtDbtInd === 'CRDT';

    // Booking date: BookgDt/Dt
    const bookgDt = ntry.getElementsByTagNameNS('*', 'BookgDt')[0];
    const date = bookgDt ? getLocalText(bookgDt, 'Dt') : null;
    if (!date) continue;

    // Sender name (debtor)
    const senderName = getLocalText(ntry, 'Nm') || '';

    // Description from remittance info
    const description = getLocalText(ntry, 'Ustrd') || '';
    // AddtlNtryInf contains full IBAN/BIC/NAME — needed for filter (e.g. MPOS BIC)
    const additionalInfo = getLocalText(ntry, 'AddtlNtryInf') || '';

    const filterReason = isCredit ? getFilterReason(senderName, description, additionalInfo) : null;
    entries.push({ date, amount, isCredit, description: description || additionalInfo, senderName, filtered: filterReason !== null, filterReason });
  }

  if (entries.length === 0) {
    throw new Error('Geen transacties gevonden in het bestand');
  }

  // Extract opening and closing balances
  const balElements = getLocalElements(stmt, 'Bal');
  let openingBalance: Camt053Balance | null = null;
  let closingBalance: Camt053Balance | null = null;

  for (const bal of balElements) {
    const code = getLocalText(bal, 'Cd'); // OPBD, CLBD, FWAV, etc.
    const amtEl = bal.getElementsByTagNameNS('*', 'Amt')[0];
    const amount = amtEl ? parseFloat(amtEl.textContent || '0') : 0;
    const cdtDbt = getLocalText(bal, 'CdtDbtInd');
    const dtEl = bal.getElementsByTagNameNS('*', 'Dt')[0];
    const date = dtEl ? getLocalText(dtEl, 'Dt') : null;
    const currency = amtEl?.getAttribute('Ccy') || 'EUR';

    // DBIT means negative balance
    const signedAmount = cdtDbt === 'DBIT' ? -amount : amount;

    if (code === 'OPBD') {
      openingBalance = { amount: signedAmount, date: date || '', currency };
    } else if (code === 'CLBD') {
      closingBalance = { amount: signedAmount, date: date || '', currency };
    }
  }

  // Aggregate credits by day (excluding filtered transactions)
  const creditsByDay = new Map<string, number>();
  let filteredTotal = 0;
  let filteredCount = 0;

  for (const entry of entries) {
    if (!entry.isCredit) continue;
    if (entry.filtered) {
      filteredTotal = Math.round((filteredTotal + entry.amount) * 100) / 100;
      filteredCount++;
      continue;
    }
    const current = creditsByDay.get(entry.date) || 0;
    creditsByDay.set(entry.date, Math.round((current + entry.amount) * 100) / 100);
  }

  return { iban, entries, creditsByDay, filteredTotal, filteredCount, openingBalance, closingBalance };
}
